import React from 'react'
import { useTranslation } from 'react-i18next'
import { RegionalPricingContext, getCountryName, getCurrencyName, getFlagEmoji } from '../../utils/currency'
import './regional-pricing-notice.css'

/**
 * One quiet line above the plan cards for visitors quoted in a non-USD currency:
 * "🇻🇳 Prices for Vietnam, billed in Vietnamese dong."
 *
 * Deliberately minimal — following the Steam/Netflix pattern, the local price is
 * simply THE price. We state the billing currency (genuinely useful: "what will
 * my card be charged in?") but never the regional discount or the geo mechanism;
 * quantifying the gap against other regions is an invitation to VPN price
 * shopping. The discountPct field stays in the API for analytics only.
 *
 * Renders nothing for USD visitors — their experience is unchanged.
 */
const RegionalPricingNotice = ({ pricing }: { pricing?: RegionalPricingContext | null }) => {
  const { t } = useTranslation('offers')
  if (!pricing || !pricing.currency || pricing.currency === 'USD') return null

  const flag = getFlagEmoji(pricing.country)
  const countryName = getCountryName(pricing.country)
  const currencyName = getCurrencyName(pricing.currency)

  return (
    <p className='regional-pricing-line'>
      {flag && (
        <span className='regional-pricing-flag' aria-hidden='true'>
          {flag}
        </span>
      )}
      {countryName
        ? t('regionalPricing.countryAndCurrency', { country: countryName, currency: currencyName })
        : t('regionalPricing.currencyOnly', { currency: currencyName })}
    </p>
  )
}

export default RegionalPricingNotice
